import React, { Children, cloneElement, FC, isValidElement, ReactNode } from 'react';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { BoxProps } from '@material-ui/core/Box';
import { PaperProps } from '@material-ui/core/Paper';

import { DraggableItemIconProps } from './DraggableItemIcon';
import { DraggableItemDivider } from './DraggableItemDivider';
import { Icons } from '../SvgIcon';
import {
  StyledContent,
  StyledDraggableItemFAIcon,
  StyledDraggableItemIcon,
  StyledWrapper,
} from './Atoms';
import { SpriteIcon } from 'src/config/api/models/FontAwesome';
import { DraggableItemFAIconProps } from './DraggableItemFAIcon';

const DefaultDraggableItemPrefix: FC<Partial<DraggableItemIconProps>> = (props) => {
  return <StyledDraggableItemIcon icon={Icons.DRAGGABLE} {...props} />;
};

// TODO - make this default with current icon??
const DraggableItemFAPrefix: FC<Partial<DraggableItemFAIconProps> & { icon: SpriteIcon }> = ({
  icon,
  ...props
}) => {
  return <StyledDraggableItemFAIcon icon={icon} {...props} />;
};

type DraggableItemProps = Omit<BoxProps & PaperProps, 'prefix' | 'postfix'> & {
  dragHandleProps?: DraggableProvidedDragHandleProps;
  prefix?: ReactNode;
  prefixFAIcon?: { icon: SpriteIcon; foregroundColor?: string; backgroundColor?: string };
  postfix?: ReactNode;
  separators?: boolean;
};

export const DraggableItem: FC<DraggableItemProps> = ({
  dragHandleProps,
  prefix,
  prefixFAIcon,
  postfix,
  separators = true,
  children,
  ...other
}) => {
  // If the container has rounded edges (is not square) the first Icon should have it's left corners rounded and the last it's right corners
  const prefixProps = { left: !other.square };
  const postfixProps = { right: !other.square };

  let draggablePrefix: ReactNode;

  if (prefixFAIcon !== undefined) {
    draggablePrefix = (
      <DraggableItemFAPrefix
        icon={prefixFAIcon.icon}
        size={'md'}
        foregroundColor={prefixFAIcon.foregroundColor}
        backgroundColor={prefixFAIcon.backgroundColor}
        {...dragHandleProps}
      />
    );
  } else if (typeof prefix !== 'undefined') {
    draggablePrefix = prefix;
  } else {
    draggablePrefix = <DefaultDraggableItemPrefix {...dragHandleProps} />;
  }

  const contentColor = other.color || 'white';
  const contentBackgroundColor = other.bgcolor || 'transparent';

  return (
    <StyledWrapper {...other}>
      {Children.map(draggablePrefix, (node, index) => {
        return isValidElement(node) && index === 0 ? cloneElement(node, prefixProps) : node;
      })}
      {separators && draggablePrefix && <DraggableItemDivider />}
      <StyledContent
        color={contentColor} // white
        bgcolor={contentBackgroundColor} // variantType === 'cluster' ? '#F9F7F5' : 'transparent'}
      >
        {children}
      </StyledContent>
      {separators && postfix && <DraggableItemDivider />}
      {Children.map(postfix, (node, index) =>
        isValidElement(node) && (!Array.isArray(postfix) || index === postfix.length - 1)
          ? cloneElement(node, postfixProps)
          : node
      )}
    </StyledWrapper>
  );
};
